import React, { useState, useEffect } from 'react';
import { TrendingUp, Calendar, Coffee, MapPin, Clock, BarChart3, Smartphone, LogIn, LogOut, Battery } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportTab() {
  const [permission, setPermission] = useState(null);
  const [summary, setSummary] = useState(null);
  const [mobile, setMobile] = useState(null);
  const [attendance, setAttendance] = useState(null);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('token');
      try {
        const [p, s, m, a] = await Promise.all([
          fetch(`${API_URL}/api/permissions/allowance`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/staff/today-summary`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/staff/mobile-hours`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/attendance/today`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (p.ok) setPermission(await p.json());
        if (s.ok) setSummary(await s.json());
        if (m.ok) setMobile(await m.json());
        if (a.ok) setAttendance(await a.json());
      } catch {}
    })();
  }, []);

  return (
    <div className="space-y-3" data-testid="report-tab">
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 size={18} />
          <h3 className="font-bold">My Report</h3>
        </div>
        <p className="text-xs text-blue-100">Quick stats for {summary?.date || 'today'}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={<MapPin size={18} />} label="Visits today" value={summary?.visits_today ?? '—'} tone="blue" />
        <StatCard icon={<Coffee size={18} />} label="Breaks today" value={summary?.breaks_today ?? '—'} tone="amber" />
        <StatCard icon={<Clock size={18} />} label="Pending tasks" value={summary?.todos_pending ?? '—'} tone="emerald" />
        <StatCard icon={<Calendar size={18} />} label="Missing log" value={summary?.missing_log_count ?? '—'} tone="red" />
        <StatCard icon={<Smartphone size={18} />} label="App hours" value={mobile ? `${mobile.app_hours}h` : '—'} tone="indigo" />
        <StatCard icon={<Battery size={18} />} label="Device hours" value={mobile ? `${mobile.device_hours}h` : '—'} tone="slate" />
      </div>

      {permission && (
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-purple-600" />
              <h3 className="font-semibold text-sm">Permission Used (this month)</h3>
            </div>
            <span className="text-[11px] text-gray-500">{permission.days_worked} days worked</span>
          </div>
          <Mini label="Used" value={`${permission.used_hours}h`} tone="text-orange-700" />
        </div>
      )}

      <SessionLog attendance={attendance} />

      <div className="bg-white rounded-xl p-4 shadow-sm text-center text-xs text-gray-500">
        <p>More personal reports coming soon.</p>
        <p className="text-[10px] text-gray-400 mt-1">Want monthly attendance / claim summary here? Tell admin to enable it.</p>
      </div>
    </div>
  );
}

function SessionLog({ attendance }) {
  // Backward-compat: build a sessions array even from legacy single-session rows.
  const sessions = (() => {
    if (!attendance) return [];
    if (Array.isArray(attendance.sessions) && attendance.sessions.length) return attendance.sessions;
    if (attendance.check_in) {
      return [{ check_in: attendance.check_in, check_out: attendance.check_out || null }];
    }
    return [];
  })();

  if (sessions.length === 0) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <Clock size={16} className="text-blue-600" />
          <h3 className="font-semibold text-sm">Today's Sessions</h3>
        </div>
        <p className="text-xs text-gray-500" data-testid="session-log-empty">
          You haven't checked in today yet.
        </p>
      </div>
    );
  }

  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
  const hoursBetween = (a, b) => {
    if (!a || !b) return null;
    const ms = new Date(b).getTime() - new Date(a).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    const h = ms / 3600000;
    return h >= 1 ? `${h.toFixed(1)}h` : `${Math.round(h * 60)}m`;
  };
  const totalClosed = sessions
    .filter(s => s.check_in && s.check_out)
    .reduce((sum, s) => sum + ((new Date(s.check_out.time) - new Date(s.check_in.time)) / 3600000), 0);

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="session-log">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-blue-600" />
          <h3 className="font-semibold text-sm">Today's Sessions</h3>
        </div>
        <span className="text-[11px] text-gray-500" data-testid="session-log-total">
          {sessions.length} session{sessions.length === 1 ? '' : 's'} · {totalClosed.toFixed(1)}h
        </span>
      </div>

      <ol className="space-y-2">
        {sessions.map((s, i) => {
          const open = !s.check_out;
          const dur = open ? null : hoursBetween(s.check_in?.time, s.check_out?.time);
          return (
            <li
              key={`sess-${s.check_in?.time || ''}-${s.check_out?.time || ''}-${i}`}
              className={`flex items-center gap-3 p-2 rounded-lg border ${
                open ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'
              }`}
              data-testid={`session-log-row-${i + 1}`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                open ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white'
              }`}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1 text-emerald-700">
                    <LogIn size={11} /> {fmt(s.check_in?.time)}
                  </span>
                  <span className="text-gray-300">→</span>
                  <span className={`flex items-center gap-1 ${open ? 'text-emerald-700 font-semibold' : 'text-red-700'}`}>
                    <LogOut size={11} />
                    {open ? 'Open' : fmt(s.check_out?.time)}
                  </span>
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {open ? 'Currently checked in' : `Worked ${dur || '—'}`}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const TONE = {
  blue: 'bg-blue-50 border-blue-200 text-blue-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  red: 'bg-red-50 border-red-200 text-red-700',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  slate: 'bg-slate-50 border-slate-200 text-slate-700'
};

function StatCard({ icon, label, value, tone }) {
  return (
    <div className={`rounded-xl border p-3 ${TONE[tone] || TONE.blue}`}>
      <div className="flex items-center justify-between">
        <span className="opacity-70">{icon}</span>
        <span className="text-2xl font-bold">{value}</span>
      </div>
      <p className="text-[11px] mt-1 opacity-80">{label}</p>
    </div>
  );
}

function Mini({ label, value, tone }) {
  return (
    <div>
      <p className="text-[10px] text-gray-500 uppercase">{label}</p>
      <p className={`text-lg font-bold ${tone}`}>{value}</p>
    </div>
  );
}
